import { useState } from "react";
import { ShieldCheck, ArrowRight, Loader2 } from "lucide-react";
import { C } from "../../theme/tokens";
import { PrimaryButton } from "../common/primitives";
import { SupportButton, TrialPeriodScreen } from "./RegistrationScreen";
import { MaintenanceNotice } from "./MaintenanceScreen";

const fieldStyle = (hasError) => ({
  width: "100%",
  background: C.panel1,
  border: `1px solid ${hasError ? "#C4604A" : C.hairline}`,
  borderRadius: 14,
  color: C.primary,
  fontFamily: "'Space Grotesk', sans-serif",
  fontWeight: 600,
  fontSize: 15,
  padding: "15px 14px",
  outline: "none",
  marginBottom: 12,
});

function Tabs({ tab, setTab, t, registrationOpen }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 4,
        background: C.panel1,
        border: `1px solid ${C.hairline}`,
        borderRadius: 14,
        padding: 4,
        marginBottom: 18,
      }}
    >
      {["login", ...(registrationOpen ? ["signup"] : [])].map((key) => (
        <button
          key={key}
          onClick={() => setTab(key)}
          style={{
            flex: 1,
            padding: "11px 10px",
            borderRadius: 10,
            border: "none",
            background: tab === key ? C.primary : "transparent",
            color: tab === key ? "#05060a" : C.secondary,
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 700,
            fontSize: 13.5,
            cursor: "pointer",
            transition: "all .15s ease",
          }}
        >
          {key === "login" ? t.auth_tab_login : t.auth_tab_signup}
        </button>
      ))}
    </div>
  );
}

/* Shown only for a plain browser visit (no Telegram WebApp context —
   see the branch in useAuth's bootstrap effect). Telegram users never
   see this: their flow still goes straight through user/auth and, for
   brand-new accounts, RegistrationScreen's Pocket-Option-ID form. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function BrowserAuthScreen({ t, error, authBusy, onLogin, onSignup, onSubmitTrial, registrationOpen = true, maintenanceMode = false, wide }) {
  const [tab, setTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accountId, setAccountId] = useState("");
  const [fullName, setFullName] = useState("");
  const [showTrial, setShowTrial] = useState(false);

  const emailValid = EMAIL_RE.test(email.trim());
  const canSubmitLogin = emailValid && password && !authBusy;
  const canSubmitSignup = emailValid && password && accountId.trim() && !authBusy;

  const handleSubmit = () => {
    if (tab === "login") {
      if (!canSubmitLogin) return;
      onLogin(email.trim().toLowerCase(), password);
    } else {
      if (!canSubmitSignup) return;
      onSignup(email.trim().toLowerCase(), password, accountId.trim(), fullName.trim());
    }
  };

  if (tab === "signup" && showTrial) {
    return (
      <TrialPeriodScreen
        t={t}
        authState={authBusy ? "linking" : "browser_auth"}
           error={error}
        onSubmit={onSubmitTrial}
        onBack={() => setShowTrial(false)}
        wide={wide}
        isBrowser={true}
        busy={authBusy}
      />
    );
  }

  const errorText =
    error === "not_found"
      ? t.auth_error_not_found
      : error === "login_taken"
      ? t.auth_error_login_taken
      : error === "account_id_not_found"
      ? t.reg_error
      : error === "password_short"
      ? t.auth_error_password_short
      : error
      ? error === "generic"
        ? t.auth_error_generic
        : t.auth_error_generic
      : null;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: wide ? 40 : 20,
      }}
    >
      <div style={{ width: "100%", maxWidth: 420 }}>
        <div
          style={{
            borderRadius: 22,
            background: `linear-gradient(160deg, ${C.panel1} 0%, ${C.panel2} 140%)`,
            border: `1px solid ${C.hairline}`,
            padding: 22,
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: C.deep,
              border: `1px solid ${C.green}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 16,
            }}
          >
            <ShieldCheck size={24} color={C.greenBright} />
          </div>

          {maintenanceMode && <MaintenanceNotice t={t} />}

          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: 21,
              color: C.primary,
              letterSpacing: "-0.01em",
              marginBottom: 8,
            }}
          >
            {tab === "login" ? t.auth_login_title : t.auth_signup_title}
          </div>
          <p style={{ color: C.secondary, fontSize: 13.5, lineHeight: 1.55, marginBottom: 18 }}>
            {tab === "login" ? t.auth_login_desc : t.auth_signup_desc}
          </p>

          <Tabs tab={tab} setTab={setTab} t={t} registrationOpen={registrationOpen} />

          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.auth_login_placeholder}
            type="email"
            inputMode="email"
            autoComplete="email"
            style={fieldStyle((errorText && (error === "not_found" || error === "login_taken")) || (email.length > 0 && !emailValid))}
          />
          <input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t.auth_password_placeholder}
            type="password"
            autoComplete={tab === "login" ? "current-password" : "new-password"}
            style={fieldStyle(false)}
          />

          {tab === "signup" && (
            <>
              <input
                value={accountId}
                onChange={(e) => setAccountId(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder={t.reg_id_placeholder}
                inputMode="numeric"
                style={fieldStyle(error === "account_id_not_found")}
              />
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={t.auth_fullname_placeholder}
                style={fieldStyle(false)}
              />
            </>
          )}

          {email.length > 0 && !emailValid && (
            <div style={{ color: "#C4604A", fontSize: 12.5, marginBottom: 8 }}>{t.auth_email_invalid}</div>
          )}

          {errorText && (
            <div style={{ color: "#C4604A", fontSize: 12.5, marginBottom: 8, textAlign: "center" }}>
              {errorText}
            </div>
          )}

          <PrimaryButton
            onClick={handleSubmit}
            disabled={tab === "login" ? !canSubmitLogin : !canSubmitSignup}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              {authBusy ? <Loader2 size={16} style={{ animation: "pjSpin 1s linear infinite" }} /> : null}
              {tab === "login" ? t.auth_login_submit : t.auth_signup_submit}
              {!authBusy && <ArrowRight size={16} />}
            </span>
          </PrimaryButton>

          {/*
          {tab === "signup" && (
            <button
              onClick={() => setShowTrial(true)}
              disabled={authBusy}
              style={{
                marginTop: 10,
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "15px 18px",
                borderRadius: 14,
                border: "none",
                background: `linear-gradient(120deg, ${C.violet} 0%, ${C.violetBright} 100%)`,
                color: "#0b0d13",
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 700,
                fontSize: 15,
                cursor: authBusy ? "not-allowed" : "pointer",
              }}
            >
              {t.reg_trial_link}
            </button>
          )}
          */}

          <SupportButton label={t.reg_support} />
        </div>
      </div>
    </div>
  );
}
